import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import * as TyreTakeoverOrderActions from './tyre-takeover-order.action';
import { TyreTakeoverOrderService } from './tyre-takeover-order.service';
import { BannerService } from '../../shared/services/banner.service';
import * as TyreTakeoverOrderModels from '../../models/tyre-takeover-order/tyre-takeover-order';
import * as OrderStatusModels from '../../models/order/orderStatus';
import * as OrderTypeModels from '../../models/order/orderType';
import { TyreTakeoverOrderSearchModel } from '../../models/tyre-takeover-order/tyre-takeover-orderSearch';
import { HelperService } from '../../../../src/app/services/helper/helper.service';
import { saveAs } from 'file-saver';
import * as Models from '../../models/commonModels';
import { StorageService } from '../../shared/services/storage.service';

export interface TyreTakeoverOrderStateModel {
  OrderStatus: OrderStatusModels.OrderStatusListModel;
  OrderTypes: OrderTypeModels.OrderTypeListModel;
  OrderList: TyreTakeoverOrderModels.TyreTakeoverOrderListModel;
  Order: Models.Response<TyreTakeoverOrderModels.TyreTakeoverOrderModel>;
  TakeoverDeposite: Models.Response<TyreTakeoverOrderModels.TakeoverDepositesModel>;
  Search: TyreTakeoverOrderSearchModel;
  ProcessingReportFiltered: number;
  ProcessingReportNFM: number;
  ImportStatus: number;
}

class DictionaryKeys {
  public static readonly OrderStatus = "TyreTakeoverOrderStatus";
}


@State<TyreTakeoverOrderStateModel>({
  name: 'TyreTakeoverOrderState',
  defaults: {
    OrderStatus: null,
    OrderTypes: null,
    OrderList: null,
    Search: null,
    Order: null,
    TakeoverDeposite: null,
    ProcessingReportFiltered: null,
    ProcessingReportNFM: null,
    ImportStatus: null
  }
})

@Injectable()

export class TyreTakeoverOrderState {

  constructor(
    private helperService: HelperService,
    private tyreTakeoverOrderService: TyreTakeoverOrderService,
    private bannerService: BannerService,
    private storageService: StorageService
  ) { }


  @Selector()
  static OrderStatus(state: TyreTakeoverOrderStateModel) {
    return state.OrderStatus;
  }

  @Selector()
  static OrderTypes(state: TyreTakeoverOrderStateModel) {
    return state.OrderTypes;
  }

  @Selector()
  static OrderList(state: TyreTakeoverOrderStateModel) {
    return state.OrderList;
  }

  @Selector()
  static Order(state: TyreTakeoverOrderStateModel) {
    return state.Order;
  }

  @Selector()
  static OrderSearch(state: TyreTakeoverOrderStateModel) {
    return state.Search;
  }

  @Selector()
  static ProcessingReportFiltered(state: TyreTakeoverOrderStateModel) {
    return state.ProcessingReportFiltered;
  }

  @Selector()
  static ProcessingReportNFM(state: TyreTakeoverOrderStateModel) {
    return state.ProcessingReportNFM;
  }

  @Selector()
  static TakeoverDeposite(state: TyreTakeoverOrderStateModel) {
    return state.TakeoverDeposite;
  }

  @Selector()
  static ImportStatus(state: TyreTakeoverOrderStateModel) {
    return state.ImportStatus;
  }

  @Action(TyreTakeoverOrderActions.GetList)
  GetList(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.GetList
  ) {

    const errorMessage = 'Błąd pobierania listy zleceń przejęcia.';

    context.setState(
      patch({
        OrderList: null
      })
    );

    this.tyreTakeoverOrderService.getList(action.Search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            OrderList: ret
          })
        );

        return ret;

      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('OrderActions.GetList', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(TyreTakeoverOrderActions.SetSearch)
  SetSearch(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.SetSearch
  ) {

    action.Search.CurrentPage = 1;

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new TyreTakeoverOrderActions.GetList(action.Search));
  }

  @Action(TyreTakeoverOrderActions.SetListPage)
  SetListPage(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.SetListPage
  ) {

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new TyreTakeoverOrderActions.GetList(action.Search));
  }

  @Action(TyreTakeoverOrderActions.ResetSearch)
  ResetSearch(
    context: StateContext<TyreTakeoverOrderStateModel>
  ) {

    const state = context.getState();

    const search = new TyreTakeoverOrderSearchModel();

    search.ItemsPerPage = state.Search.ItemsPerPage;

    context.setState(
      patch({
        Search: search
      }));

    context.dispatch(new TyreTakeoverOrderActions.GetList(search));
  }

  @Action(TyreTakeoverOrderActions.GetStatusDictionary)
  GetStatusDictionary(
    context: StateContext<TyreTakeoverOrderStateModel>
  ) {

    const errorMessage = 'Błąd pobierania statusów zleceń przejęcia opon.';

    let storageKey = DictionaryKeys.OrderStatus;

    context.setState(
      patch({
        OrderStatus: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.OrderStatus) {

      this.tyreTakeoverOrderService.getStatusDictionary().subscribe((ret) => {

        if (ret.Success) {

          context.setState(
            patch({
              OrderStatus: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }

      },
        (error) => {
          this.helperService.SaveException('TyreTakeoverOrderActions.GetStatusDictionary', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(TyreTakeoverOrderActions.DisposeSearch)
  DisposeSearch(
    context: StateContext<TyreTakeoverOrderStateModel>
  ) {

    context.setState(
      patch({
        Search: null
      }));
  }

  @Action(TyreTakeoverOrderActions.Set)
  Set(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.Set
  ) {
    const errorMessage = 'Błąd zapisu zmian zlecenia przejęcia opon.';

    this.tyreTakeoverOrderService.set(action.Order).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Zapisano zmiany do zlecenia przejęcia opon ' + ret.Item.Number + '.');

          context.setState(
            patch({
              Order: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.Set', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(TyreTakeoverOrderActions.Get)
  Get(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.Get
  ) {
    const errorMessage = 'Błąd odczytu szczegółów zlecenia przejęcia opon.';

    this.tyreTakeoverOrderService.get(action.OrderId).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              Order: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.Get', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(TyreTakeoverOrderActions.Create)
  Create(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.Create
  ) {
    const errorMessage = 'Błąd utworzenia zlecenia przejęcia opon.';

    this.tyreTakeoverOrderService.create(action.Order).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Utworzono zlecenia przejęcia opon ' + ret.Item.Number + '.');
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }

        context.setState(
          patch({
            Order: ret
          })
        );
      },

      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.Create', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(TyreTakeoverOrderActions.CreateBulk)
  CreateBulk(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.CreateBulk
  ) {
    const errorMessage = 'Błąd utworzenia hurtowego zlecenia przejęcia opon.';

    this.tyreTakeoverOrderService.createBulk(action.Order).subscribe(
      ret => {

        action.Order.End();

        if (ret.Success) {
          this.bannerService.success('Utworzono hurtowe zlecenia przejęcia opon ' + ret.Item.Number + '.');
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }

        context.setState(
          patch({
            Order: ret
          })
        );
      },

      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.CreateBulk', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(TyreTakeoverOrderActions.SetAuthorizationFile)
  SetAuthorizationFile(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.SetAuthorizationFile
  ) {

    let errorMessage = 'Błąd przesyłania dokumentu upoważnienia.';

    this.tyreTakeoverOrderService.setAuthorizationFile(action.Order, action.File).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Dokument upoważnienia zapisany.');
        }
        else {
          this.bannerService.error(ret.Message);
        }

        context.setState(
          patch({
            Order: ret
          })
        );
      },

      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.SetAuthorizationFile', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(TyreTakeoverOrderActions.GetAuthorizationFile)
  GetAuthorizationFile(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.GetAuthorizationFile
  ) {

    this.bannerService.info('Rozpoczęto pobieranie upoważnienia.');

    this.tyreTakeoverOrderService.getAuthorizationFile(action.Order).subscribe(
      res => {

        const file = new File([res.body], this.helperService.GetFileNameFromHttpResponse(res), { type: res.headers.get('Content-Type') });

        saveAs(file);
      },
      res => {
        this.bannerService.error(`Błąd pobierania upoważnienia.`);
      },
      () => {
        this.helperService.consoleLog('TyreTakeoverOrderActions.GetAuthorizationFile');
      }
    );
  }

  @Action(TyreTakeoverOrderActions.SetProtocolFile)
  SetProtocolFile(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.SetProtocolFile
  ) {

    let errorMessage = 'Błąd przesyłania protokołu.';

    this.tyreTakeoverOrderService.setProtocolFile(action.Order, action.File).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Protokół zapisany.');
        }
        else {
          this.bannerService.error(ret.Message);
        }

        context.setState(
          patch({
            Order: ret
          })
        );
      },

      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.SetProtocolFile', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(TyreTakeoverOrderActions.GetProtocolFile)
  GetProtocolFile(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.GetAuthorizationFile
  ) {

    this.bannerService.info('Rozpoczęto pobieranie protokołu.');

    this.tyreTakeoverOrderService.getProtocolFile(action.Order).subscribe(
      res => {

        const file = new File([res.body], this.helperService.GetFileNameFromHttpResponse(res), { type: res.headers.get('Content-Type') });

        saveAs(file);
      },
      res => {
        this.bannerService.error(`Błąd pobierania protokołu.`);
      },
      () => {
        this.helperService.consoleLog('TyreTakeoverOrderActions.GetProtocolFile');
      }
    );
  }

  @Action(TyreTakeoverOrderActions.DeleteTakeoverDeposite)
  DeleteTakeoverDeposite(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.DeleteTakeoverDeposite
  ) {
    const errorMessage = 'Błąd usunięcia zestawu opon.';

    this.tyreTakeoverOrderService.deleteDeposite(action.TakeoverDeposite).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Usunięto zestaw opon.');

          context.setState(
            patch({
              TakeoverDeposite: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.DeleteTakeoverDeposite', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(TyreTakeoverOrderActions.AddTakeoverDeposite)
  AddTakeoverDeposite(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.DeleteTakeoverDeposite
  ) {
    const errorMessage = 'Błąd dodawania zestawu opon.';

    this.tyreTakeoverOrderService.addDeposite(action.TakeoverDeposite).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Dodano zestaw opon.');

          context.setState(
            patch({
              TakeoverDeposite: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.AddTakeoverDeposite', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(TyreTakeoverOrderActions.HandoverProtocolPdf)
  HandoverProtocolPdf(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.HandoverProtocolPdf
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie pliku pdf, proszę czekać.');

    this.tyreTakeoverOrderService.getHandoverProtocolPdf(action.Order).subscribe(ret => {
      const file = new File([ret], `Protokół-zdawczo-odbiorczy-${this.helperService.TimeStamp()}.pdf`, { type: 'application/pdf' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.HandoverProtocolPdf', error);

        this.bannerService.error('Błąd pobierania pliku pdf.');
      },
      () => {
        //context.setState(
        //  patch({
        //    ProcessingPdf: Math.random()
        //  })
        //);

      }
    );
  }

  @Action(TyreTakeoverOrderActions.ProtocolXls)
  ProtocolXls(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.ProtocolXls
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie pliku xlsx, proszę czekać.');

    this.tyreTakeoverOrderService.getProtocolXls(action.Order).subscribe(ret => {
      const file = new File([ret], `Protokół-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('TyreTakeoverOrderActions.ProtocolXls', error);

        this.bannerService.error('Błąd pobierania pliku xlsx.');
      },
      () => {
        //context.setState(
        //  patch({
        //    ProcessingPdf: Math.random()
        //  })
        //);

      }
    );
  }

  @Action(TyreTakeoverOrderActions.ImportTyresFile)
  ImportTyresFile(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.ImportTyresFile
  ) {

    this.bannerService.quickInfo('Rozpoczęto import opon do zamówienia, proszę czekać.');


    this.tyreTakeoverOrderService.importTyreFile(action.Order, action.File).subscribe(ret => {

      this.helperService.consoleLog(ret.headers);

      const processed = ret.headers.get('x-import-processed');
      const error = ret.headers.get('x-import-error');
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      if (result === 'OK') {
        const file = new File([ret.body], `Wynik-Importu-${this.helperService.TimeStamp()}-${action.File.name}`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        if (imported > 0) {
          this.bannerService.instantSuccess(`Zakończono import pliku <b>${action.File.name}</b><br/><b>Zaimportowano ${imported}</b> pozycji.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }
        else {
          this.bannerService.instantMessage(`Zakończono import pliku <b>${action.File.name}</b><br/><b>Nie zaimporotwano żadnych pozycji</b>.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }

        saveAs(file);

        let orderRequest = new Models.RequestModel(action.Order.Item.OrderId);
        
        context.dispatch(new TyreTakeoverOrderActions.Get(orderRequest));

      } else {

        if (message === 'IMPORT_EXCEPTION_EMPTY_FILE') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.File.name}</b><br/>Plik przeznaczony do importu jest pusty.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_FILE_EXTENSION') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.File.name}</b><br/>Plik przeznaczony do importu ma niewłaściwe rozszerzenie.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_HEDER') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.File.name}</b><br/>Plik przeznaczony do importu ma niewłaściwy nagłówek.`);
        } else {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.File.name}</b><br/>Wystąpił nieznany błąd podczas przetwarzania pliku.`);
        }
      }
    },
      (error) => {

        this.helperService.SaveException('TyreOrderActions.Import', error);

        this.bannerService.error('Błąd importu opon do zamówienia.');

        //context.dispatch(new TyreOrderActions.SetImportStatus(false));
      },
      () => {
        //context.dispatch(new TyreOrderActions.SetImportStatus(false));
      }
    );
  }

  @Action(TyreTakeoverOrderActions.ImportOrders)
  ImportOrder(
    context: StateContext<TyreTakeoverOrderStateModel>,
    action: TyreTakeoverOrderActions.ImportOrders
  ) {

    this.bannerService.quickInfo('Rozpoczęto import zleceń zamówień opon, proszę czekać.');


    this.tyreTakeoverOrderService.importOrders(action.Order, action.File).subscribe(ret => {

      this.helperService.consoleLog(ret.headers);

      const processed = ret.headers.get('x-import-processed');
      const error = ret.headers.get('x-import-error');
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      if (result === 'OK') {
        const file = new File([ret.body], `Wynik-Importu-${this.helperService.TimeStamp()}-${action.File.name}`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        if (imported > 0) {
          this.bannerService.instantSuccess(`Zakończono import pliku <b>${action.File.name}</b><br/><b>Zaimportowano ${imported}</b> pozycji.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }
        else {
          this.bannerService.instantMessage(`Zakończono import pliku <b>${action.File.name}</b><br/><b>Nie zaimporotwano żadnych pozycji</b>.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }

        saveAs(file);
      } else {

        if (message === 'IMPORT_EXCEPTION_EMPTY_FILE') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.File.name}</b><br/>Plik przeznaczony do importu jest pusty.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_FILE_EXTENSION') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.File.name}</b><br/>Plik przeznaczony do importu ma niewłaściwe rozszerzenie.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_HEDER') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.File.name}</b><br/>Plik przeznaczony do importu ma niewłaściwy nagłówek.`);
        } else {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.File.name}</b><br/>Wystąpił nieznany błąd podczas przetwarzania pliku.`);
        }
      }
    },
      (error) => {

        this.helperService.SaveException('TyreOrderActions.Import', error);

        this.bannerService.error('Błąd importu zleceń zamówień opon.');
      },
      () => {
      }
    );
  }
}
